import type { EmpNotification } from "../../../../shared/employee/notifications";

/**
 * One line for a bell item, in plain words. A moderator is never named: the
 * moderation types read the same whoever acted.
 */
export function notificationText(
  n: Pick<EmpNotification, "type" | "actor" | "entityType">,
): string {
  const who = n.actor?.name ?? "Someone";
  const entityIsComment = n.entityType === "comment";
  switch (n.type) {
    case "mention":
      return `${who} mentioned you`;
    case "comment":
      return `${who} commented on your post`;
    case "reply":
      return `${who} replied to your comment`;
    case "like":
      return entityIsComment ? `${who} liked your comment` : `${who} liked your post`;
    case "shout_out":
      return `${who} gave you a shout-out`;
    case "ecard":
      return `${who} sent you a card`;
    case "milestone":
      return `${who} has a milestone today`;
    case "giftcard_codes":
      return `${who} changed gift card codes`;
    case "gift_awarded":
      return `${who} gave you a reward`;
    case "gift_approval":
      return `An award from ${who} is waiting for approval`;
    case "gift_low_stock":
      return "A reward is running low";
    case "gift_ready":
      return "Your reward is ready to pick up";
    case "gift_cancelled":
      return "A reward you were given was cancelled";
    case "resource_ack":
      return "A policy needs your acknowledgement";
    case "resource_ack_reminder":
      return "Reminder: a policy needs your acknowledgement";
    case "resource_review":
      return "A resource you own is due for review";
    case "gift_expiring":
      return "A gift card you haven't opened expires soon";
    case "gift_decided":
      return n.entityType === "award_declined"
        ? `${who} declined your award`
        : `${who} approved your award`;
    case "survey":
      return "A survey is waiting for you";
    case "message":
      // Named, but never quoted. The bell is read over shoulders and on lock
      // screens, and a private message is the one thing here that must not
      // show its contents to whoever is standing nearby.
      return `${who} sent you a message`;
    case "announcement":
      return n.actor ? `New announcement from ${who}` : "New announcement";
    case "poll":
      return n.actor ? `New poll from ${who}` : "New poll";
    case "post_hidden":
      return "A moderator hid your post";
    case "post_removed":
      return entityIsComment ? "A moderator removed your comment" : "A moderator removed your post";
    case "report_outcome":
      return "Your report was reviewed";
    case "broadcast":
      // The snippet under it carries the title; the organisation is the sender.
      return n.actor ? `New message from ${who}` : "New message from your organisation";
    case "broadcast_approval":
      return `A broadcast from ${who} is waiting for approval`;
    case "broadcast_decided":
      return n.entityType === "broadcast_declined"
        ? "Your broadcast was declined"
        : n.entityType === "broadcast_withdrawn"
          ? "Your broadcast wasn't approved in time"
          : "Your broadcast was approved";
    case "broadcast_reply_in":
      return `${who} replied to a broadcast`;
    case "broadcast_reply":
      return "Your organisation answered your reply";
    case "automation_notice":
      return "A note about one of your people";
    case "automation_paused":
      return "An automation you run has stopped";
    case "texting_paused":
      return "Texts are paused: this month's budget is spent";
  }
}

/**
 * A notification's link with the instance's query string added: joined with
 * "&" when the link has its own (?resource=, ?tab=), so neither is lost.
 */
export function withInstanceQuery(link: string, query: string): string {
  const extra = query.replace(/^\?/, "");
  if (!extra) return link;
  return `${link}${link.includes("?") ? "&" : "?"}${extra}`;
}

/** Only in-app skyEmployee paths are followed from the bell. */
export function safeNotificationLink(link: string | null): string | null {
  if (!link) return null;
  return /^\/employee\/[^/]/.test(link) && !link.includes("//") ? link : null;
}
